/**
 * The "put this on your home screen" prompt for staff.
 *
 * A web app CANNOT install itself. Browsers require a deliberate human gesture,
 * and the two platforms differ:
 *
 *   Android, Chrome and Edge (phone and desktop): the browser fires
 *   `beforeinstallprompt`. We capture it, show our own card, and one tap runs
 *   the real install dialog. That is as close to automatic as exists.
 *
 *   iPhone and iPad: Safari has NO install API at all. The only route is
 *   Share, then Add to Home Screen, done by hand. All we can do is say so
 *   clearly at the moment it is relevant.
 *
 * So this shows the best available path per platform rather than pretending
 * one exists. It never appears once the app is already installed, and a
 * dismissal is remembered so it cannot nag somebody every shift.
 */
import { useEffect, useState } from 'react';
import { Share, Plus, Download, X } from 'lucide-react';

const DISMISSED = 'sp.install.dismissed';

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS reports it here rather than through display-mode.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

const isIos = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent); // only Safari can add to home screen

export function InstallApp() {
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISSED) === '1';
    } catch {
      // A locked down browser throws on storage. Showing the prompt is the
      // safe side of that failure.
    }
    if (dismissed) return;

    if (isIos()) {
      setShow(true);
      return;
    }
    const onPrompt = (e: Event) => {
      // Keep the event so our own button can trigger the real dialog later.
      e.preventDefault();
      setDeferred(e as InstallEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => setShow(false);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // The prompt also appears on desktop Chrome and Edge, where "on your phone"
  // would simply be wrong.
  const onPhone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches || window.innerWidth < 820);

  if (!show) return null;

  const close = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISSED, '1');
    } catch {
      /* nothing to do, it just asks again next time */
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    setDeferred(null);
    close();
  };

  return (
    <div
      className="mx-4 mb-3 mt-3 p-3.5"
      style={{
        borderRadius: 'var(--sp-radius-lg)',
        background: 'var(--sp-accent-soft)',
        border: '1px solid var(--sp-accent-border)',
        boxShadow: 'var(--sp-shadow-1)',
      }}
      role="region"
      aria-label="Install the Sneaky Panda app"
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center"
          style={{ borderRadius: 'var(--sp-radius)', background: 'var(--sp-surface)' }}
          aria-hidden="true"
        >
          <Download size={17} style={{ color: 'var(--sp-accent-strong)' }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold text-ink">
            Install the app on your {onPhone ? 'phone' : 'computer'}
          </p>
          {deferred ? (
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">
              Sneaky Panda installs like any other app. Full screen, its own icon, one tap away.
            </p>
          ) : (
            <p className="mt-1 flex flex-wrap items-center gap-1 text-[12.5px] leading-relaxed text-muted">
              <span>To install it, tap</span>
              <Share size={13} aria-label="the Share button" style={{ color: 'var(--sp-accent-strong)' }} />
              <span>at the bottom of Safari, then</span>
              <Plus size={13} aria-hidden="true" style={{ color: 'var(--sp-accent-strong)' }} />
              <span className="font-semibold text-ink">Add to Home Screen</span>
            </p>
          )}
          {deferred ? (
            <button type="button" className="btn btn-primary btn-sm mt-2.5" onClick={() => void install()}>
              <Download size={14} aria-hidden="true" />
              Install the app
            </button>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-icon btn-sm btn-ghost shrink-0"
          onClick={close}
          title="Not now|You can install it later from your browser menu."
          aria-label="Dismiss"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
